// src/lib/eventDateHelpers.ts

export type FooEvents = {
  WooCommerceEventsType?: string;

  // Single / sequential
  WooCommerceEventsDate?: string;
  WooCommerceEventsEndDate?: string;
  WooCommerceEventsDateMySQLFormat?: string;
  WooCommerceEventsEndDateMySQLFormat?: string;
  WooCommerceEventsDateTimeTimestamp?: string | number;
  WooCommerceEventsDateTimestamp?: string | number;
  WooCommerceEventsEndDateTimestamp?: string | number;
  WooCommerceEventsEndDateTimeTimestamp?: string | number;
  WooCommerceEventsHour?: string;
  WooCommerceEventsMinutes?: string;
  WooCommerceEventsPeriod?: string;
  WooCommerceEventsHourEnd?: string;
  WooCommerceEventsMinutesEnd?: string;
  WooCommerceEventsEndPeriod?: string;

  // Specific days (type = "select")
  WooCommerceEventsNumDays?: string;
  WooCommerceEventsSelectDate?: string[];
  WooCommerceEventsSelectDateTimestamps?: string[];
  WooCommerceEventsSelectDateHour?: string[];
  WooCommerceEventsSelectDateMinutes?: string[];
  WooCommerceEventsSelectDatePeriod?: string[];
  WooCommerceEventsSelectDateHourEnd?: string[];
  WooCommerceEventsSelectDateMinutesEnd?: string[];
  WooCommerceEventsSelectDatePeriodEnd?: string[];

  WooCommerceEventsTimeZone?: string;
  WooCommerceEventsLocation?: string;
};

export type FooEventsSlot = {
  id?: string;
  booking_date_id?: string;
  label?: string;
  date?: string | null;
  time?: string | null;
  datetime?: string | null;
  stock?: number | string | null;
};

export type FooEventsSlots = {
  timezone?: string;
  slots?: FooEventsSlot[];
};

export interface FooEventsProduct {
  fooevents?: FooEvents | null;
  fooevents_slots?: FooEventsSlots | null;
}

export type EventKind = "single" | "sequential" | "select" | "slot" | "unknown";

// ─── Low-level parsers ────────────────────────────────────────────────────────

export function parseMysqlDateTime(input?: string | null): Date | null {
  if (!input) return null;
  const m = String(input).match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], m[6] ? +m[6] : 0);
  return isNaN(d.getTime()) ? null : d;
}

export function parseIsoDateTime(input?: string | null): Date | null {
  if (!input) return null;
  const d = new Date(String(input));
  return isNaN(d.getTime()) ? null : d;
}

export function parseYmdDate(input?: string | null): Date | null {
  if (!input) return null;
  const m = String(input).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
  return isNaN(d.getTime()) ? null : d;
}

export function parseDdmyDate(input?: string | null): Date | null {
  if (!input) return null;
  const m = String(input).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const d = new Date(+m[3], +m[2] - 1, +m[1], 0, 0, 0, 0);
  return isNaN(d.getTime()) ? null : d;
}

export function combineDateAndTime(
  dateStr?: string | null,
  hour?: string | null,
  minute?: string | null,
  period?: string | null,
): Date | null {
  if (!dateStr) return null;
  const m = String(dateStr).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;

  let h = hour != null && hour !== "" ? parseInt(hour, 10) : 0;
  const mm = minute != null && minute !== "" ? parseInt(minute, 10) : 0;

  if (period) {
    const p = String(period).toLowerCase().replace(/[.\s]/g, "");
    if (p === "pm" && h < 12) h += 12;
    if (p === "am" && h === 12) h = 0;
  }

  if (isNaN(h) || isNaN(mm)) return null;

  const d = new Date(+m[3], +m[2] - 1, +m[1], h, mm, 0, 0);
  return isNaN(d.getTime()) ? null : d;
}

// ─── FooEvents field readers ──────────────────────────────────────────────────

export function getFooEventsStartDate(p: FooEventsProduct): Date | null {
  const fe = p.fooevents ?? null;
  if (!fe) return null;
  const d1 = parseMysqlDateTime(
    typeof fe.WooCommerceEventsDateMySQLFormat === "string"
      ? fe.WooCommerceEventsDateMySQLFormat
      : null
  );
  if (d1) return d1;
  const tsRaw = fe.WooCommerceEventsDateTimeTimestamp ?? fe.WooCommerceEventsDateTimestamp;
  const ts =
    typeof tsRaw === "string" ? parseInt(tsRaw, 10) : typeof tsRaw === "number" ? tsRaw : NaN;
  if (!isNaN(ts) && ts > 0) {
    const d2 = new Date(ts * 1000);
    return isNaN(d2.getTime()) ? null : d2;
  }
  return null;
}

function getSequentialEndDate(fe: FooEvents): Date | null {
  const mysql = parseMysqlDateTime(fe.WooCommerceEventsEndDateMySQLFormat ?? null);
  if (mysql) return mysql;

  const combined = combineDateAndTime(
    fe.WooCommerceEventsEndDate,
    fe.WooCommerceEventsHourEnd,
    fe.WooCommerceEventsMinutesEnd,
    fe.WooCommerceEventsEndPeriod,
  );
  if (combined) return combined;

  const tsRaw = fe.WooCommerceEventsEndDateTimeTimestamp ?? fe.WooCommerceEventsEndDateTimestamp;
  const ts =
    typeof tsRaw === "string" ? parseInt(tsRaw, 10) : typeof tsRaw === "number" ? tsRaw : NaN;
  if (!isNaN(ts) && ts > 0) {
    const d = new Date(ts * 1000);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function getSelectStartDates(fe: FooEvents): Date[] {
  const dates = Array.isArray(fe.WooCommerceEventsSelectDate) ? fe.WooCommerceEventsSelectDate : [];
  const hours = Array.isArray(fe.WooCommerceEventsSelectDateHour) ? fe.WooCommerceEventsSelectDateHour : [];
  const mins = Array.isArray(fe.WooCommerceEventsSelectDateMinutes) ? fe.WooCommerceEventsSelectDateMinutes : [];
  const periods = Array.isArray(fe.WooCommerceEventsSelectDatePeriod) ? fe.WooCommerceEventsSelectDatePeriod : [];

  const out: Date[] = [];
  for (let i = 0; i < dates.length; i++) {
    const d = combineDateAndTime(dates[i], hours[i], mins[i], periods[i]);
    if (d) out.push(d);
  }
  return out.sort((a, b) => a.getTime() - b.getTime());
}

function getSelectEndDates(fe: FooEvents): Date[] {
  const dates = Array.isArray(fe.WooCommerceEventsSelectDate) ? fe.WooCommerceEventsSelectDate : [];
  const hours = Array.isArray(fe.WooCommerceEventsSelectDateHourEnd) ? fe.WooCommerceEventsSelectDateHourEnd : [];
  const mins = Array.isArray(fe.WooCommerceEventsSelectDateMinutesEnd) ? fe.WooCommerceEventsSelectDateMinutesEnd : [];
  const periods = Array.isArray(fe.WooCommerceEventsSelectDatePeriodEnd) ? fe.WooCommerceEventsSelectDatePeriodEnd : [];

  const out: Date[] = [];
  for (let i = 0; i < dates.length; i++) {
    const d = combineDateAndTime(dates[i], hours[i], mins[i], periods[i]);
    if (d) out.push(d);
  }
  return out.sort((a, b) => a.getTime() - b.getTime());
}

// ─── Kind detection ───────────────────────────────────────────────────────────

export function getEventKind(p: FooEventsProduct): EventKind {
  const fe = p.fooevents ?? null;
  const slots = p.fooevents_slots?.slots || [];

  // Explicit event type wins over stale slot data.
  // A product with WooCommerceEventsType = "single" | "sequential" | "select"
  // must not be treated as a bookings/slot event even if fooevents_slots is
  // populated from stale fooevents_bookings_options_serialized meta.
  const t = (fe?.WooCommerceEventsType ?? "").toLowerCase();
  if (t === "single") return "single";
  if (t === "sequential") return "sequential";
  if (t === "select") return "select";

  // Only treat as slot/bookings when the type says so, or when there's
  // no explicit type at all and slots are present.
  if (slots.length > 0) return "slot";

  return "unknown";
}

// ─── Public start/end/upcoming ────────────────────────────────────────────────

export function getEventStart(p: FooEventsProduct): Date | null {
  const fe = p.fooevents ?? null;
  const slots = p.fooevents_slots?.slots || [];
  const kind = getEventKind(p);

  if (kind === "slot") {
    const ds = slots
      .map((s) => parseIsoDateTime(s?.datetime || null) || parseYmdDate(s?.date || null))
      .filter((d): d is Date => d !== null)
      .sort((a, b) => a.getTime() - b.getTime());
    if (ds.length) return ds[0];
  }

  if (kind === "select" && fe) {
    const ds = getSelectStartDates(fe);
    if (ds.length) return ds[0];
  }

  return getFooEventsStartDate(p);
}

export function getEventEnd(p: FooEventsProduct): Date | null {
  const fe = p.fooevents ?? null;
  const slots = p.fooevents_slots?.slots || [];
  const kind = getEventKind(p);

  if (kind === "slot") {
    const ds = slots
      .map((s) => parseIsoDateTime(s?.datetime || null) || parseYmdDate(s?.date || null))
      .filter((d): d is Date => d !== null)
      .sort((a, b) => b.getTime() - a.getTime());
    if (ds.length) return ds[0];
  }

  if (kind === "select" && fe) {
    const ds = getSelectEndDates(fe);
    if (ds.length) return ds[ds.length - 1];
  }

  if (kind === "sequential" && fe) {
    const end = getSequentialEndDate(fe);
    if (end) return end;
  }

  return getFooEventsStartDate(p);
}

export function isUpcoming(p: FooEventsProduct): boolean {
  const end = getEventEnd(p);
  if (!end) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return end.getTime() >= today.getTime();
}

// ─── Display label ────────────────────────────────────────────────────────────

const toMidnight = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

export function getEventDateLabel(
  p: FooEventsProduct,
  locale: string,
  options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "long", year: "numeric" },
  labelOptions: { compact?: boolean } = {},
): string {
  const fe = p.fooevents ?? null;
  const slots = p.fooevents_slots?.slots || [];
  const kind = getEventKind(p);
  const fmt = new Intl.DateTimeFormat(locale, options);
  const { compact = false } = labelOptions;

  if (kind === "slot") {
    const days = slots
      .map((s) => parseYmdDate(s?.date || null) || parseIsoDateTime(s?.datetime || null))
      .filter((d): d is Date => d !== null)
      .map(toMidnight)
      .sort((a, b) => a.getTime() - b.getTime());
    if (days.length) {
      const a = fmt.format(days[0]);
      const b = fmt.format(days[days.length - 1]);
      return a === b ? a : `${a} – ${b}`;
    }
  }

  if (kind === "select" && fe) {
    const dates = getSelectStartDates(fe).map(toMidnight);
    if (dates.length === 0) {
      const start = getFooEventsStartDate(p);
      const end = getSequentialEndDate(fe);
      if (start && end) {
        const a = fmt.format(toMidnight(start));
        const b = fmt.format(toMidnight(end));
        return a === b ? a : `${a} – ${b}`;
      }
      if (start) return fmt.format(toMidnight(start));
      return "";
    }
    if (dates.length === 1) return fmt.format(dates[0]);
    if (compact) {
      const a = fmt.format(dates[0]);
      const b = fmt.format(dates[dates.length - 1]);
      return a === b ? a : `${a} – ${b}`;
    }
    return dates.map((d) => fmt.format(d)).join(", ");
  }

  if (kind === "sequential" && fe) {
    const start = getFooEventsStartDate(p);
    const end = getSequentialEndDate(fe);
    if (start && end) {
      const a = fmt.format(toMidnight(start));
      const b = fmt.format(toMidnight(end));
      return a === b ? a : `${a} – ${b}`;
    }
    if (start) return fmt.format(toMidnight(start));
    return "";
  }

  const start = getFooEventsStartDate(p);
  if (!start) return "";
  return fmt.format(toMidnight(start));
}

// ─── Per-day rows (for product page detail views) ─────────────────────────────

export type EventDayRow = {
  date: Date;
  start: Date;
  end: Date | null;
};

export function getEventDayRows(p: FooEventsProduct): EventDayRow[] {
  const fe = p.fooevents ?? null;
  const slots = p.fooevents_slots?.slots || [];
  const kind = getEventKind(p);

  if (kind === "slot") {
    return slots
      .flatMap((s): EventDayRow[] => {
        const start = parseIsoDateTime(s?.datetime || null) || parseYmdDate(s?.date || null);
        if (!start) return [];
        return [{ date: toMidnight(start), start, end: null }];
      })
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  }

  if (kind === "select" && fe) {
    const starts = getSelectStartDates(fe);
    const ends = getSelectEndDates(fe);
    return starts.map((start, i) => ({
      date: toMidnight(start),
      start,
      end: ends[i] ?? null,
    }));
  }

  if (kind === "sequential" && fe) {
    const start = getFooEventsStartDate(p);
    const end = getSequentialEndDate(fe);
    if (!start) return [];
    const first = toMidnight(start);
    const last = end ? toMidnight(end) : first;

    const rows: EventDayRow[] = [];
    const cursor = new Date(first);
    let guard = 0;
    while (cursor.getTime() <= last.getTime() && guard < 366) {
      const dd = `${String(cursor.getDate()).padStart(2, "0")}/${String(cursor.getMonth() + 1).padStart(2, "0")}/${cursor.getFullYear()}`;

      const dayStart = combineDateAndTime(
        dd,
        fe.WooCommerceEventsHour,
        fe.WooCommerceEventsMinutes,
        fe.WooCommerceEventsPeriod,
      ) || new Date(cursor);

      const dayEnd = combineDateAndTime(
        dd,
        fe.WooCommerceEventsHourEnd,
        fe.WooCommerceEventsMinutesEnd,
        fe.WooCommerceEventsEndPeriod,
      );

      rows.push({ date: new Date(cursor), start: dayStart, end: dayEnd });
      cursor.setDate(cursor.getDate() + 1);
      guard++;
    }
    return rows;
  }

  const start = getFooEventsStartDate(p);
  if (!start) return [];
  const end = fe
    ? combineDateAndTime(
        fe.WooCommerceEventsDate,
        fe.WooCommerceEventsHourEnd,
        fe.WooCommerceEventsMinutesEnd,
        fe.WooCommerceEventsEndPeriod,
      )
    : null;
  return [{ date: toMidnight(start), start, end }];
}